import { inject, provide, ref } from 'vue';
import { fetchUtmParams } from '../api/CustomersApi';
import { UtmParams } from '../types/utmParams';

interface UseUtm {
  utmParams: UtmParams | undefined;
  setUtmParams: (params: UtmParams | undefined) => void;
  getUtmParams: () => Promise<UtmParams | undefined>;
}

const LOCAL_STORAGE_KEY = 'utmParams';

const utmParams = ref<UtmParams | undefined>(undefined);

// Setter function to manually set UTM params and store them in localStorage
const setUtmParams = (params: UtmParams | undefined) => {
  utmParams.value = params;
  if (params) {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(params));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
};

const extractUtmFromQuery = (): UtmParams | undefined => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Partial<UtmParams> = {};

  // Add the desired UTM fields from query params
  if (urlParams.has('utm_source')) { utmParams.utm_source = urlParams.get('utm_source')!; }
  if (urlParams.has('utm_medium')) { utmParams.utm_medium = urlParams.get('utm_medium')!; }
  if (urlParams.has('utm_campaign')) { utmParams.utm_campaign = urlParams.get('utm_campaign')!; }
  if (urlParams.has('utm_term')) { utmParams.utm_term = urlParams.get('utm_term')!; }
  if (urlParams.has('utm_content')) { utmParams.utm_content = urlParams.get('utm_content')!; }

  return Object.keys(utmParams).length ? (utmParams as UtmParams) : undefined;
};

// Getter function to retrieve UTM params from localStorage, memory, or fetch from API
const getUtmParams = async (): Promise<UtmParams | undefined> => {
  if (utmParams.value) {
    return utmParams.value;
  }

  if (window.location.search) {
    const utmFromQuery = extractUtmFromQuery();
    if (utmFromQuery) {
      utmParams.value = utmFromQuery;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(utmFromQuery));
      return utmParams.value;
    }
  }

  const storedParams = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (storedParams) {
    utmParams.value = JSON.parse(storedParams);
    return utmParams.value;
  }

  const data = await fetchUtmParams(); // Fetch from API if not found in memory or localStorage
  utmParams.value = data;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
  return utmParams.value;
};

export const useUtmService = () => {
  provide('utmParams', utmParams);
  provide('fetchUtmParams', fetchUtmParams);
  provide('setUtmParams', setUtmParams);
  provide('getUtmParams', getUtmParams);
};

export const useUtm = (): UseUtm => ({
  utmParams: inject('utmParams')!,
  setUtmParams: inject('setUtmParams')!,
  getUtmParams: inject('getUtmParams')!,
});
